import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { cx } from '@emotion/css';
import { CircularProgress } from '@mui/material';
import { Autocomplete, TextField } from '@procurenetworks/procure-component-library';
import { useClient } from 'urql';
import FormLabel from 'app/components/Form/FormLabel';
import {
  AssetItemSchema,
  InventoryItemSchema,
  ItemFilters,
  ItemTypeEnum,
  MediaSchema,
  SortOrderEnum,
} from 'app/types/schema';
import Box from 'app/ui-components/Box';

import FormError from '../../../../components/Form/FormError';
import { textValidator } from '../../../../components/Form/utils/validators';
import Assets from '../../../../i18n/Assets';
import { getSupportedImageAttachments } from '../../../../utils/attachments';
import { TransformResponseResult,useOptimizedItemSearch } from '../../../../utils/itemSearchCache';
import {
  ItemInStockSelectDocument,
  ItemInStockSelectQuery,
  ItemInStockSelectQueryVariables,
  useItemInStockSelectQuery,
} from '../../graphql/query/generated/itemInStockSelect';
import Inventory from 'app/i18n/Inventory';

type Option =
  | Pick<InventoryItemSchema, 'id' | 'sku' | 'title' | 'attachments'>
  | Pick<AssetItemSchema, 'id' | 'sku' | 'title' | 'attachments'>;

interface SelectProps {
  className?: string;
  error?: FieldError | undefined;
  label?: string;
  isRequired?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  value: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onChange: (values: string, option?: Option) => void;
  placeholder?: string;
  types?: ItemTypeEnum[];
  filters?: ItemFilters;
  renderInput?: (params: any) => React.ReactElement;
  pickableThroughOrderRequest?: boolean;
  tenantIds?: string[];
  partnerTenantId?: string;
  name?: string;
}

const getOptionLabel = (option: Option) => `${option.title} (${option.sku})`;

export const FormItemInStock = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const {
    name,
    className,
    label,
    types,
    value,
    isRequired,
    disabled,
    onBlur,
    onChange,
    error,
    placeholder = '',
    autoFocus,
    filters,
    onFocus,
    renderInput,
    pickableThroughOrderRequest,
    tenantIds,
    partnerTenantId,
  } = props;

  const client = useClient();
  const [localInputValue, setLocalInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<Option | null>(null);

  const searchFilters = useMemo(() => ({
    types: types || [],
    ...filters,
    pickableThroughOrderRequest,
    tenantIds: tenantIds || undefined,
    partnerTenantId,
  }), [types, filters, pickableThroughOrderRequest, tenantIds, partnerTenantId]);

  // --- Logic for fetching selected item's details (if value is provided) ---
  const [{ fetching: selectedItemFetching, data: selectedData }] = useItemInStockSelectQuery({
    variables: {
      filters: {
        itemIds: value ? [value] : [],
        pickableThroughOrderRequest,
        tenantIds: tenantIds || undefined,
        partnerTenantId,
      },
      limit: 1,
    },
    pause: !value || Boolean(value && selectedValue && selectedValue.id === value),
  });

  useEffect(() => {
    if (value && selectedData?.items.edges?.[0]?.node) {
      const selectedNode = selectedData.items.edges[0].node as Option;
      setSelectedValue(selectedNode);
    } else if (!value) {
      setSelectedValue(null);
    }
  }, [value, selectedData, setSelectedValue]);
  
  useEffect(() => {
    if (value === '') {
      setSelectedValue(null);
    }
  }, [value]);

  // --- Setup for useOptimizedItemSearch ---
  const transformResponse = useCallback(
    (
      response: ItemInStockSelectQuery,
      itemsPerPageFromHook: number
    ): TransformResponseResult<Option> => {
      const items = response.items?.edges?.map(({ node }) => {
        // Ensure attachments is always an array, even if null/undefined from GQL
        return {
          ...node,
          attachments: ('attachments' in node && node.attachments) ? node.attachments as MediaSchema[] : [],
        } as Option;
      }) || [];
      // Use fallback logic since pageInfo is not available in this query
      const hasNextPage = items.length === itemsPerPageFromHook;
      return {
        items,
        hasNextPage,
      };
    },
    []
  );

  const getVariables = useCallback(
    (
      currentSearchTerm: string,
      currentFilters: Record<string, any>,
      page: number,
      limit: number
    ): ItemInStockSelectQueryVariables => {
      return {
        filters: {
          search: currentSearchTerm || undefined,
          ...currentFilters,
        },
        sorts: [
          { sortField: 'sku', sortOrder: SortOrderEnum.Asc },
          { sortField: 'title', sortOrder: SortOrderEnum.Asc },
        ],
        limit: limit,
        page: page,
      };
    },
    []
  );

  const executeQueryWrapper = useCallback(
    async (variables: ItemInStockSelectQueryVariables) => {
      const result = await client
        .query<ItemInStockSelectQuery, ItemInStockSelectQueryVariables>(
          ItemInStockSelectDocument,
          variables
        )
        .toPromise();
      if (result.error) throw result.error;
      if (!result.data) throw new Error('No data returned from query');
      return result.data;
    },
    [client]
  );

  const { displayedItems, isLoadingInitial, isLoadingMore, hasNextPage, loadMore, error: searchHookError, setSearchTerm } = 
    useOptimizedItemSearch<Option, ItemInStockSelectQueryVariables, ItemInStockSelectQuery>(
      '',
      searchFilters,
      {
        allowEmptySearch: true,
        itemsPerPage: 20,
        executeQuery: executeQueryWrapper,
        transformResponse,
        getVariables,
        debounceDelay: 300,
      }
    );

  useEffect(() => {
    if (value && selectedValue) {
      const initialLabel = getOptionLabel(selectedValue);
      setLocalInputValue(initialLabel);
      setSearchTerm(initialLabel);
    } else if (!value) {
        setLocalInputValue('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedValue]);

  useEffect(() => {
    if (value === '') {
      setSelectedValue(null);
      setLocalInputValue('');
      setSearchTerm('');
    }
  }, [value, setSearchTerm]);

  if (searchHookError) {
    console.error("Search query error from hook:", searchHookError);
  }

  const onValueChangeCallback = useCallback(
    (event: React.SyntheticEvent, option: Option | null) => {
      if (option) {
        setSelectedValue(option);
        onChange(option.id, option);
        const label = getOptionLabel(option);
        setLocalInputValue(label); 
      } else {
        setSelectedValue(null);
        onChange('', undefined);
        setLocalInputValue(''); 
        setSearchTerm('');
      }
    },
    [onChange, setSearchTerm]
  );

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (listboxNode.scrollTop + listboxNode.clientHeight + 1 >= listboxNode.scrollHeight) {
      if (hasNextPage && !isLoadingMore) {
        loadMore();
      }
    }
  };

  const inputRender = (params: any) => {
    if (renderInput) {
      return renderInput({ ...params, error: Boolean(error || searchHookError) });
    }

    return (
      <TextField 
        {...params} 
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {(isLoadingInitial || isLoadingMore || selectedItemFetching) ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }} 
        autoFocus={autoFocus} 
        error={!!error || !!searchHookError}
        helperText={error?.message || searchHookError?.message}
        name={name}
        placeholder={placeholder}
      />
    );
  };

  return (
    <Box ref={ref} className={cx('flex flex-col', className)}>
      {label ? <FormLabel isRequired={isRequired}>{label}</FormLabel> : null}
      <Autocomplete
        ListboxProps={{
          onScroll: handleScroll,
        }}
        className={cx(
          'mt-6 rounded text-13 text-[#495057] focus:border-[#80bdff] focus:outline-none',
          className,
        )}
        disabled={disabled || selectedItemFetching}
        filterOptions={(option) => option}
        getOptionLabel={getOptionLabel}
        inputValue={localInputValue}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        label={''}
        loading={isLoadingInitial || isLoadingMore || selectedItemFetching}
        options={displayedItems}
        renderInput={inputRender}
        renderOption={(props: any, option: Option) => {
          const attachments = (option.attachments || []) as MediaSchema[];
          const imageAttachments = getSupportedImageAttachments(attachments);
          const imageUrl = imageAttachments.length ? imageAttachments[0].url : undefined;
          const showImageBox = selectedValue?.id !== option?.id;
          return (
            <li key={option?.id} className="flex" {...props} style={{ display: 'flex', alignItems: 'center' }}>
              {showImageBox ? (
                imageUrl ? (
                  <img
                    alt={option.title}
                    className="mw-[50px] mh-[50px] mr-[10px] h-[50px] w-[50px] object-contain"
                    loading="lazy"
                    src={imageUrl}
                  />
                ) : (
                  <div className="mw-[50px] mh-[50px] mr-[10px] h-[50px] w-[50px] flex items-center justify-center bg-gray-100 text-gray-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )
              ) : undefined}
              <span>
                {option.title} ({option.sku})
              </span>
            </li>
          );
        }}
        value={selectedValue}
        onBlur={() => {
          setLocalInputValue(selectedValue ? selectedValue.sku : '');
          onBlur?.();
        }}
        onChange={onValueChangeCallback}
        onFocus={onFocus}
        onInputChange={(event, newInputValue, reason) => {
          setLocalInputValue(newInputValue);
          if (reason === 'input') {
            setSearchTerm(newInputValue);
          } else if (reason === 'clear') {
            setSearchTerm('');
          }
        }}
      />
      {error && <FormError error={error} />}
      {searchHookError && !error && <FormError error={{ message: searchHookError.message }} />}
    </Box>
  );
});

interface Props {
  control: any;
  types?: ItemTypeEnum[];
  filters?: ItemFilters;
  name?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onChange?: (values: string, option?: Option) => void;
}
const ItemInStockSelect = (props: Props) => {
  const { control, name = 'skuIds', types, autoFocus, disabled, filters, onChange } = props;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItemInStock
          {...field}
          isRequired
          autoFocus={autoFocus}
          className="flex-1"
          disabled={disabled}
          error={fieldState.error}
          filters={filters}
          label={Assets.FormLabels.ItemInStock}
          types={types}
          onChange={(value, option) => {
            field.onChange(value);
            onChange?.(value, option);
          }}
        />
      )}
      rules={{
        validate: textValidator('PROCURE_8012'),
      }}
    />
  );
};

export default ItemInStockSelect;
